import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaWhatsapp,
  FaTwitter,
  FaInstagram,
  FaTelegram,
  FaFacebookF,
} from "react-icons/fa6";
import { MdEmail, MdPhone, MdLocationOn, MdArrowUpward } from "react-icons/md";
import { GiTwoCoins, GiPerspectiveDiceSixFacesRandom } from "react-icons/gi";
import { HiSparkles } from "react-icons/hi2";
import { CAPITAL, COUNTRY, WHATSAPP, PHONENUMBER } from "../config/appConfig";

const Footer = () => {
  const [hoveredSocial, setHoveredSocial] = useState(null);
  const currentYear = new Date().getFullYear();

  // Contact info
  // const contacts = {
  //   whatsapp: "260760579796",
  //   phone: "+260760579796",
  //   email: "support@coinflipzambia.com",
  //   location: `Lusaka, ${COUNTRY}`,
  // };

  // Social links
  const socialLinks = [
    {
      name: "WhatsApp",
      icon: FaWhatsapp,
      href: `https://wa.me/${WHATSAPP}`,
      color: "#25D366",
      hoverBg: "hover:bg-[#25D366]",
    },
    // {
    //   name: "Twitter",
    //   icon: FaTwitter,
    //   href: "https://twitter.com/coinflipzambia",
    //   color: "#1DA1F2",
    //   hoverBg: "hover:bg-[#1DA1F2]",
    // },
    // {
    //   name: "Instagram",
    //   icon: FaInstagram,
    //   href: "https://instagram.com/coinflipzambia",
    //   color: "#E4405F",
    //   hoverBg: "hover:bg-[#E4405F]",
    // },
    // {
    //   name: "Telegram",
    //   icon: FaTelegram,
    //   href: "https://t.me/coinflipzambia",
    //   color: "#0088CC",
    //   hoverBg: "hover:bg-[#0088CC]",
    // },
    // {
    //   name: "Facebook",
    //   icon: FaFacebookF,
    //   href: "https://facebook.com/coinflipzambia",
    //   color: "#1877F2",
    //   hoverBg: "hover:bg-[#1877F2]",
    // },
  ];

  // Quick links
  const quickLinks = [
    { name: "Play Now", href: "/" },
    { name: "How to Play", href: "/how" },
    { name: "FAQs", href: "/faqs" },
    { name: "Profile", href: "/profile" },
  ];

  // Legal links
  const legalLinks = [
    { name: "Terms of Service", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Responsible Gaming", href: "/responsible-gaming" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <footer className="relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#071129] to-[#050d1d]" />

      {/* Floating particles effect - hidden on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-emerald-500/20"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 30}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Glowing orbs - hidden on mobile */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl hidden sm:block" />

      {/* Top border glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      <div className="relative z-10">
        {/* Main Footer Content */}
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-8">
            {/* Brand Section - Centered on mobile */}
            <motion.div
              variants={itemVariants}
              className="lg:col-span-1 text-center md:text-left"
            >
              <div className="flex items-center gap-3 mb-6 justify-center md:justify-start">
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 p-0.5">
                    <div className="w-full h-full rounded-xl bg-[#071129] flex items-center justify-center">
                      <img
                        src="images/transparentLogo.webp"
                        alt="game logo"
                        className="rounded-md"
                      />
                      {/* <GiTwoCoins className="w-6 h-6 text-emerald-400" /> */}
                    </div>
                  </div>
                  <HiSparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 animate-pulse" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    Heads or Tails {COUNTRY}
                  </h3>
                  <p className="text-xs text-gray-500 tracking-wider uppercase">
                    Pick • Flip • Win
                  </p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs mx-auto md:mx-0">
                Zambia&apos;s live coin flip game. Choose Heads or Tails, stake
                what you want, and get paid when your side lands — on web or
                USSD *464*1#.
              </p>

              {/* App badges - centered on mobile */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-400 flex items-center gap-2">
                  <GiPerspectiveDiceSixFacesRandom className="text-emerald-400" />
                  Provably Fair
                </span>
                {/* <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-400">
                  🇿🇲 Made in {COUNTRY}
                </span> */}
              </div>
            </motion.div>

            {/* Quick Links - Centered on mobile */}
            <motion.div
              variants={itemVariants}
              className="text-center md:text-left"
            >
              <h4 className="text-white font-semibold mb-4 sm:mb-6 flex items-center gap-2 justify-center md:justify-start">
                <span className="w-8 h-0.5 bg-gradient-to-r from-emerald-400 to-transparent rounded-full hidden md:block" />
                Quick Links
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      onClick={scrollToTop}
                      className="group inline-flex items-center text-gray-400 hover:text-emerald-400 transition-all duration-300"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-emerald-400 mr-0 group-hover:mr-2 transition-all duration-300 hidden md:block" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Legal Links - Centered on mobile */}
            <motion.div
              variants={itemVariants}
              className="text-center md:text-left"
            >
              <h4 className="text-white font-semibold mb-4 sm:mb-6 flex items-center gap-2 justify-center md:justify-start">
                <span className="w-8 h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded-full hidden md:block" />
                Legal
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      onClick={scrollToTop}
                      className="group inline-flex items-center text-gray-400 hover:text-blue-400 transition-all duration-300"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-blue-400 mr-0 group-hover:mr-2 transition-all duration-300 hidden md:block" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Age restriction badge - centered on mobile */}
              <div className="mt-4 sm:mt-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl max-w-[250px] mx-auto md:mx-0">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold text-sm flex-shrink-0">
                    18+
                  </div>
                  <p className="text-xs text-gray-400 text-left">
                    Adults only. Gamble responsibly.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Section - Centered on mobile */}
            <motion.div
              variants={itemVariants}
              className="text-center md:text-left"
            >
              <h4 className="text-white font-semibold mb-4 sm:mb-6 flex items-center gap-2 justify-center md:justify-start">
                <span className="w-8 h-0.5 bg-gradient-to-r from-cyan-400 to-transparent rounded-full hidden md:block" />
                Get in Touch
              </h4>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex justify-center md:justify-start">
                  <a
                    href={`https://wa.me/${WHATSAPP}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 text-gray-400 hover:text-emerald-400 transition-all duration-300"
                  >
                    <span className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-emerald-500/20 flex items-center justify-center transition-all duration-300">
                      <FaWhatsapp className="w-5 h-5" />
                    </span>
                    <span className="text-sm">{WHATSAPP}</span>
                  </a>
                </li>
                <li className="flex justify-center md:justify-start">
                  <a
                    href={`tel:${PHONENUMBER}`}
                    className="group inline-flex items-center gap-3 text-gray-400 hover:text-blue-400 transition-all duration-300"
                  >
                    <span className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-blue-500/20 flex items-center justify-center transition-all duration-300">
                      <MdPhone className="w-5 h-5" />
                    </span>
                    <span className="text-sm">{PHONENUMBER}</span>
                  </a>
                </li>
                {/* <li className="flex justify-center md:justify-start">
                  <a
                    href={`mailto:${contacts.email}`}
                    className="group inline-flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-all duration-300"
                  >
                    <span className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-cyan-500/20 flex items-center justify-center transition-all duration-300">
                      <MdEmail className="w-5 h-5" />
                    </span>
                    <span className="text-sm text-left break-all">{contacts.email}</span>
                  </a>
                </li> */}
                <li className="flex items-center gap-3 text-gray-400 justify-center md:justify-start">
                  <span className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                    <MdLocationOn className="w-5 h-5" />
                  </span>
                  <span className="text-sm">
                    {CAPITAL}, {COUNTRY}
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Social Links Bar */}
          <motion.div
            variants={itemVariants}
            className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/5"
          >
            <div className="flex flex-col items-center gap-4 sm:gap-6">
              <p className="text-gray-500 text-sm">Connect with us</p>
              <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group"
                    onHoverStart={() => setHoveredSocial(social.name)}
                    onHoverEnd={() => setHoveredSocial(null)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div
                      className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center transition-all duration-300 group-hover:border-transparent ${social.hoverBg} group-hover:text-white`}
                      style={{
                        color:
                          hoveredSocial === social.name ? "#fff" : social.color,
                      }}
                    >
                      <social.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 bg-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col items-center gap-4">
              {/* Copyright - centered */}
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-gray-500 text-xs sm:text-sm text-center">
                <span>
                  © {currentYear} Heads or Tails {COUNTRY}.
                </span>
                <span className="hidden sm:inline">•</span>
                <span>All rights reserved.</span>
              </div>

              {/* Bottom row with ZynlePay and back to top */}
              <div className="flex items-center justify-center gap-4">
                {/* Payment methods placeholder */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Powered by</span>
                  <div className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-md">
                    <span className="text-xs font-medium bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                      PCH
                    </span>
                  </div>
                </div>

                {/* Back to top button */}
                <motion.button
                  onClick={scrollToTop}
                  className="group w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-emerald-500/50 transition-all duration-300"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MdArrowUpward className="w-4 h-4 sm:w-5 sm:h-5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
